import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, AlertTriangle, Database, Eye, HardDrive, Users, TrendingUp, RefreshCw, CheckCircle2, XCircle, Clock, MinusCircle, Save, Search, Target } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const BulkForceSyncCard = () => {
  const [syncing, setSyncing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [activeCompany, setActiveCompany] = useState(null);
  const [result, setResult] = useState(null);
  const [discoverResult, setDiscoverResult] = useState(null);
  const [fromDatetime, setFromDatetime] = useState('');
  // Manual linking state: { [tenantId_fasigName]: { selectedSaleId, saving, saved } }
  const [linkState, setLinkState] = useState({});
  // All live Fasig sales per tenant fetched after discover
  const [fasigSalesByTenant, setFasigSalesByTenant] = useState({});

  const handleForceSync = async (company) => {
    setSyncing(true); setActiveCompany(company); setResult(null);
    try {
      const body = fromDatetime ? { from_datetime: new Date(fromDatetime).toISOString() } : {};
      const res = await api.post(`/repository-sync/admin/force-sync/${company}`, body);
      setResult({ ...res.data, company });
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || 'Sync failed', company });
    }
    setSyncing(false); setActiveCompany(null);
  };

  const handleDiscover = async () => {
    setDiscovering(true); setDiscoverResult(null); setLinkState({}); setFasigSalesByTenant({});
    try {
      const [discRes] = await Promise.all([
        api.post('/repository-sync/admin/discover-sale-names'),
      ]);
      setDiscoverResult(discRes.data);
      const byTenant = {};
      // Fetch all live sales per tenant (system-wide + tenant-specific)
      for (const r of (discRes.data.results || [])) {
        if (r.tenant_id && r.status !== 'error' && r.status !== 'skipped') {
          try {
            const salesRes = await api.get(`/repository-sync/admin/tenant-sales/${r.tenant_id}`);
            byTenant[r.tenant_id] = salesRes.data || [];
          } catch { byTenant[r.tenant_id] = []; }
        }
      }
      setFasigSalesByTenant(byTenant);
    } catch (e) {
      setDiscoverResult({ error: e?.response?.data?.detail || 'Discovery failed' });
    }
    setDiscovering(false);
  };

  const handleLink = async (tenantId, fasigName, saleId) => {
    const key = `${tenantId}|${fasigName}`;
    setLinkState(prev => ({ ...prev, [key]: { ...prev[key], saving: true } }));
    try {
      await api.put('/repository-sync/admin/link-fasig-sale', { sale_id: saleId, fasig_sale_name: fasigName });
      setLinkState(prev => ({ ...prev, [key]: { selectedSaleId: saleId, saving: false, saved: true } }));
      toast.success('Sale linked');
    } catch {
      toast.error('Failed to link');
      setLinkState(prev => ({ ...prev, [key]: { ...prev[key], saving: false } }));
    }
  };

  const handleUnlink = async (tenantId, fasigName, saleId) => {
    const key = `${tenantId}|${fasigName}`;
    setLinkState(prev => ({ ...prev, [key]: { ...prev[key], saving: true } }));
    try {
      await api.put('/repository-sync/admin/link-fasig-sale', { sale_id: saleId, fasig_sale_name: '' });
      setLinkState(prev => ({ ...prev, [key]: { selectedSaleId: '', saving: false, saved: false } }));
      toast.success('Unlinked');
    } catch {
      toast.error('Failed to unlink');
      setLinkState(prev => ({ ...prev, [key]: { ...prev[key], saving: false } }));
    }
  };

  return (
    <Card className="border-dashed mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Database className="w-4 h-4 text-muted-foreground" /> Bulk Force Sync
        </CardTitle>
        <CardDescription className="text-xs">
          Trigger an immediate sync for all tenants and all active sales per company.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Shared date/time range for backfill — applies to any company's force sync */}
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground whitespace-nowrap">From (optional):</label>
          <Input type="datetime-local" value={fromDatetime} onChange={e => setFromDatetime(e.target.value)}
            className={`h-8 text-xs w-44 ${fromDatetime ? 'date-filled' : ''}`} />
          {fromDatetime && (
            <Button size="sm" variant="ghost" className="h-8 text-xs px-2 text-muted-foreground"
              onClick={() => setFromDatetime('')}>Clear</Button>
          )}
        </div>
        {/* All force sync buttons on one row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
            onClick={() => handleForceSync('fasig')} disabled={syncing || discovering}>
            {syncing && activeCompany === 'fasig' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Force Sync — Fasig-Tipton
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
            onClick={() => handleForceSync('obs')} disabled={syncing || discovering}>
            {syncing && activeCompany === 'obs' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Force Sync — OBS
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
            onClick={() => handleForceSync('keeneland')} disabled={syncing || discovering}>
            {syncing && activeCompany === 'keeneland' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Force Sync — Keeneland
          </Button>
        </div>
        <div className="pt-1 border-t">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={handleDiscover} disabled={syncing || discovering}>
            {discovering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Discover Fasig Sale Names
          </Button>
          <p className="text-[11px] text-muted-foreground mt-1">
            Queries Fasig without a sale name filter to find active sale names, auto-links by hip overlap, and lets you manually correct any links.
          </p>
        </div>

        {/* Force sync result */}
        {result && !result.error && (
          <p className="text-xs text-green-700">
            {result.company === 'obs'
              ? `OBS: synced ${result.tenants_synced} tenant(s) — ${result.total_procedures_created} new procedure(s) created.`
              : `Synced ${result.tenants_synced} tenant(s) — ${result.total_procedures_created} new procedure(s) created.`}
          </p>
        )}
        {result?.error && <p className="text-xs text-red-600">{result.error}</p>}

        {/* Discover results with manual linking */}
        {discoverResult && !discoverResult.error && (
          <div className="space-y-3">
            {discoverResult.results?.map((r, i) => (
              <div key={i} className="p-3 border rounded bg-muted/10 space-y-2">
                <p className="text-xs font-semibold">{r.tenant_name}</p>
                {r.status === 'error' && <p className="text-xs text-red-600">{r.error}</p>}
                {r.status === 'skipped' && <p className="text-xs text-muted-foreground">No credentials configured</p>}
                {r.status !== 'error' && r.status !== 'skipped' && r.discovered_sale_names?.length === 0 && (
                  <p className="text-xs text-muted-foreground">No activity found in today's window</p>
                )}
                {r.discovered_sale_names?.map((fasigName, j) => {
                  const autoMatch = r.updates?.find(u => u.fasig_name === fasigName);
                  const key = `${r.tenant_id}|${fasigName}`;
                  const ls = linkState[key] || {};
                  const sales = fasigSalesByTenant[r.tenant_id] || [];
                  const linkedSaleId = ls.selectedSaleId !== undefined ? ls.selectedSaleId : (autoMatch?.sale_id || '');
                  const linkedSaleName = sales.find(s => s.id === linkedSaleId)?.sale_name || autoMatch?.sale_display_name || '';
                  const isLinked = !!(linkedSaleId && (ls.saved || autoMatch));

                  return (
                    <div key={j} className="border rounded p-2 bg-background space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded text-[11px] shrink-0">{fasigName}</span>
                        {isLinked
                          ? <span className="text-xs text-green-700">→ linked to "{linkedSaleName}"</span>
                          : <span className="text-xs text-amber-600">→ not linked</span>}
                      </div>
                      {/* Manual link row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select
                          value={linkedSaleId || '__none__'}
                          onValueChange={val => setLinkState(prev => ({ ...prev, [key]: { ...prev[key], selectedSaleId: val === '__none__' ? '' : val, saved: false } }))}
                        >
                          <SelectTrigger className="h-7 text-xs w-52">
                            <SelectValue placeholder="Select a sale to link..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__" className="text-xs text-muted-foreground">— No link —</SelectItem>
                            {sales.map(s => (
                              <SelectItem key={s.id} value={s.id} className="text-xs">{s.sale_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(ls.selectedSaleId !== undefined || autoMatch) && (
                          ls.selectedSaleId
                            ? <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                                disabled={ls.saving}
                                onClick={() => handleLink(r.tenant_id, fasigName, ls.selectedSaleId)}>
                                {ls.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Link'}
                              </Button>
                            : isLinked && <Button size="sm" variant="outline" className="h-7 text-xs text-destructive border-destructive/30"
                                disabled={ls.saving}
                                onClick={() => handleUnlink(r.tenant_id, fasigName, linkedSaleId)}>
                                {ls.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Unlink'}
                              </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {discoverResult?.error && <p className="text-xs text-red-600">{discoverResult.error}</p>}
      </CardContent>
    </Card>
  );
};

// ── Per-Sale Force Sync ───────────────────────────────────────────────────────
const COMPANY_OPTIONS = [
  { value: 'keeneland', label: 'Keeneland' },
  { value: 'obs',       label: 'OBS' },
  { value: 'fasig',     label: 'Fasig-Tipton' },
];

const PerSaleForceSyncCard = () => {
  const [company,   setCompany]   = useState('');
  const [tenants,   setTenants]   = useState([]);
  const [tenantId,  setTenantId]  = useState('');
  const [sales,     setSales]     = useState([]);
  const [saleId,    setSaleId]    = useState('');
  const [syncing,   setSyncing]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingSales,   setLoadingSales]   = useState(false);

  const handleCompanyChange = async (val) => {
    setCompany(val); setTenantId(''); setTenants([]); setSaleId(''); setSales([]); setResult(null);
    if (!val) return;
    setLoadingTenants(true);
    try {
      const res = await api.get(`/repository-sync/admin/tenants-by-company/${val}`);
      setTenants(res.data || []);
    } catch { toast.error('Failed to load tenants'); }
    setLoadingTenants(false);
  };

  const handleTenantChange = async (val) => {
    setTenantId(val); setSaleId(''); setSales([]); setResult(null);
    if (!val || !company) return;
    setLoadingSales(true);
    try {
      const res = await api.get(`/repository-sync/admin/sales-for-sync?tenant_id=${val}&company=${company}`);
      setSales(res.data || []);
    } catch { toast.error('Failed to load sales'); }
    setLoadingSales(false);
  };

  const handleSync = async () => {
    if (!company || !tenantId || !saleId) return;
    setSyncing(true); setResult(null);
    try {
      const res = await api.post('/repository-sync/admin/sync-sale', { company, tenant_id: tenantId, sale_id: saleId });
      setResult({ ok: true, ...res.data });
      toast.success(`Sync complete — ${res.data.procedures_created ?? res.data.total_created ?? 0} new procedure(s)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Sync failed';
      setResult({ ok: false, error: msg });
      toast.error(msg);
    }
    setSyncing(false);
  };

  const selectedSale = sales.find(s => s.id === saleId);

  return (
    <Card className="border-dashed mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Target className="w-4 h-4 text-muted-foreground" /> Per-Sale Force Sync
        </CardTitle>
        <CardDescription className="text-xs">
          Sync a specific tenant's data for a single sale — bypasses the active-window guard.
          Useful for historic or practice sales.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 flex-wrap items-end">
          {/* Company */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Company</label>
            <Select value={company} onValueChange={handleCompanyChange}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="per-sale-company-select">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Tenant */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Tenant</label>
            <Select value={tenantId} onValueChange={handleTenantChange} disabled={!company || loadingTenants}>
              <SelectTrigger className="h-8 text-xs w-48" data-testid="per-sale-tenant-select">
                <SelectValue placeholder={loadingTenants ? 'Loading…' : 'Select tenant…'} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map(t => <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>)}
                {tenants.length === 0 && !loadingTenants && company &&
                  <div className="px-3 py-2 text-xs text-muted-foreground">No tenants with {company} credentials</div>}
              </SelectContent>
            </Select>
          </div>
          {/* Sale */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Sale</label>
            <Select value={saleId} onValueChange={v => { setSaleId(v); setResult(null); }} disabled={!tenantId || loadingSales}>
              <SelectTrigger className="h-8 text-xs w-64" data-testid="per-sale-sale-select">
                <SelectValue placeholder={loadingSales ? 'Loading…' : 'Select sale…'} />
              </SelectTrigger>
              <SelectContent>
                {sales.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-xs">
                    {s.sale_name}{s.sale_start_date ? ` (${s.sale_start_date})` : ''}
                  </SelectItem>
                ))}
                {sales.length === 0 && !loadingSales && tenantId &&
                  <div className="px-3 py-2 text-xs text-muted-foreground">No non-closed {company} sales for this tenant</div>}
              </SelectContent>
            </Select>
          </div>
          {/* Sync button */}
          <Button size="sm" className="h-8 text-xs gap-1.5 bg-primary hover:bg-primary/90 text-white"
            onClick={handleSync} disabled={syncing || !company || !tenantId || !saleId}
            data-testid="per-sale-sync-btn">
            {syncing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</> : <><RefreshCw className="w-3.5 h-3.5" /> Sync This Sale</>}
          </Button>
        </div>
        {result?.ok && (
          <p className="text-xs text-green-700">
            Synced <strong>{selectedSale?.sale_name}</strong> — {result.procedures_created ?? result.total_created ?? 0} new procedure(s) created.
          </p>
        )}
        {result?.ok === false && <p className="text-xs text-red-600">{result.error}</p>}
      </CardContent>
    </Card>
  );
};


const SaleWindowETACard = () => {
  const [scraping, setScraping] = useState(false);
  const [etaResult, setEtaResult] = useState(null);

  const handleForceETA = async () => {
    setScraping(true); setEtaResult(null);
    try {
      const res = await api.post('/sale-window/admin/trigger-keeneland');
      setEtaResult(res.data);
      if (res.data.results?.length) {
        toast.success(`ETA scrape complete — ${res.data.total_hips} hips across ${res.data.sales_scraped} sale(s)`);
      } else {
        toast.info(res.data.message || 'No active Keeneland sales with a table link configured.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'ETA scrape failed';
      setEtaResult({ error: msg });
      toast.error(msg);
    } finally {
      setScraping(false);
    }
  };

  return (
    <Card className="border-dashed mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-muted-foreground" /> Sale Window ETA — Keeneland
        </CardTitle>
        <CardDescription className="text-xs">
          Force an immediate catalog scrape to update live ring-time estimates for all active Keeneland sales with a table URL configured.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
          onClick={handleForceETA} disabled={scraping} data-testid="force-eta-keeneland-btn">
          {scraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Force Scrape ETA — Keeneland
        </Button>
        {etaResult && !etaResult.error && etaResult.results?.map((r, i) => (
          <p key={i} className={`text-xs ${r.ok ? 'text-green-700' : 'text-red-600'}`}>
            {r.ok
              ? `${r.sale_name}: ${r.hips_scraped} hips scraped`
              : `${r.sale_name}: ${r.error}`}
          </p>
        ))}
        {etaResult?.message && <p className="text-xs text-muted-foreground">{etaResult.message}</p>}
        {etaResult?.error && <p className="text-xs text-red-600">{etaResult.error}</p>}
      </CardContent>
    </Card>
  );
};

const MonitoringPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errorFilter, setErrorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState([]);
  const [errorLogMeta, setErrorLogMeta] = useState({});
  const [hideCancelled, setHideCancelled] = useState(true);
  const [selectedLogIds, setSelectedLogIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');

  const ERROR_STATUSES = [
    { value: 'new',         label: 'New',         color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { value: 'in_progress', label: 'In Progress',  color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { value: 'on_hold',     label: 'On Hold',      color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { value: 'resolved',    label: 'Resolved',     color: 'bg-green-100 text-green-700 border-green-200' },
    { value: 'canceled',    label: 'Canceled',     color: 'bg-gray-100 text-gray-500 border-gray-200' },
  ];

  const getStatusStyle = (status) =>
    ERROR_STATUSES.find(s => s.value === status)?.color || ERROR_STATUSES[0].color;

  // ── Primary monitoring data ───────────────────────────────────────────────
  const { data: monitoringData, isLoading: loading } = useQuery({
    queryKey: QK.monitoringData(),
    queryFn: async () => {
      const [impersonation, errors, resources, tenantsRes] = await Promise.all([
        api.get('/impersonation/logs'),
        api.get('/monitoring/errors'),
        api.get('/monitoring/resources'),
        api.get('/tenants'),
      ]);
      const errData = errors.data || { logs: [], summary: {} };
      const meta = {};
      for (const log of errData.logs || []) {
        if (log.id) meta[log.id] = { status: log.status || 'new', notes: log.notes || '' };
      }
      return {
        impersonationLogs: impersonation.data || [],
        errorLogs: errData,
        resourceUsage: resources.data || [],
        tenants: tenantsRes.data || [],
        initialMeta: meta,
      };
    },
  });
  const impersonationLogs = monitoringData?.impersonationLogs ?? [];
  const errorLogs         = monitoringData?.errorLogs         ?? { logs: [], summary: {} };
  const resourceUsage     = monitoringData?.resourceUsage     ?? [];
  const tenants           = monitoringData?.tenants           ?? [];

  // Seed per-log meta from query data on first load
  useEffect(() => {
    if (monitoringData?.initialMeta) setErrorLogMeta(monitoringData.initialMeta);
  }, [monitoringData?.errorLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => qc.invalidateQueries({ queryKey: QK.monitoringData() });

  const handleStatusChange = async (logId, newStatus) => {
    setErrorLogMeta(prev => ({ ...prev, [logId]: { ...prev[logId], status: newStatus } }));
    try {
      await api.patch(`/monitoring/errors/${logId}`, { status: newStatus });
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleNotesSave = async (logId) => {
    const notes = errorLogMeta[logId]?.notes ?? '';
    setErrorLogMeta(prev => ({ ...prev, [logId]: { ...prev[logId], savingNotes: true } }));
    try {
      await api.patch(`/monitoring/errors/${logId}`, { notes });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setErrorLogMeta(prev => ({ ...prev, [logId]: { ...prev[logId], savingNotes: false } }));
    }
  };

  // ── Sync logs — on-demand, filter-driven (stay raw — not cacheable) ───────
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLogsLoading, setSyncLogsLoading] = useState(false);
  const [syncTenantFilter, setSyncTenantFilter] = useState('all');
  const [syncCompanyFilter, setSyncCompanyFilter] = useState('all');
  const [syncSaleNameFilter, setSyncSaleNameFilter] = useState('');
  const [syncPage, setSyncPage] = useState(1);
  const [syncTotal, setSyncTotal] = useState(0);
  const SYNC_PAGE_SIZE = 100;

  useEffect(() => {
    setSyncPage(1);
    fetchSyncLogs(1);
  }, [syncTenantFilter, syncCompanyFilter, syncSaleNameFilter]); // eslint-disable-line

  const fetchSyncLogs = async (page) => {
    const p = page ?? syncPage;
    setSyncLogsLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: SYNC_PAGE_SIZE });
      if (syncTenantFilter !== 'all') params.append('tenant_id', syncTenantFilter);
      if (syncCompanyFilter !== 'all') params.append('company', syncCompanyFilter);
      if (syncSaleNameFilter.trim()) params.append('sale_name', syncSaleNameFilter.trim());
      const res = await api.get(`/repository-sync/admin/logs?${params}`);
      const data = res.data || {};
      setSyncLogs(data.logs || []);
      setSyncTotal(data.total || 0);
    } catch {}
    setSyncLogsLoading(false);
  };

  const COMPANY_LABEL = { fasig: 'Fasig-Tipton', obs: 'OBS', keeneland: 'Keeneland', inglis: 'Inglis' };

  const getFilteredErrorLogs = () => {
    let logs = errorLogs.logs;
    if (errorFilter !== 'all') logs = logs.filter(log => log.severity === errorFilter);
    if (statusFilter.length > 0) {
      logs = logs.filter(log => {
        const currentStatus = errorLogMeta[log.id]?.status || log.status || 'new';
        return statusFilter.includes(currentStatus);
      });
    }
    // Hide cancelled entries by default — toggle to reveal
    if (hideCancelled) {
      logs = logs.filter(log => {
        const currentStatus = errorLogMeta[log.id]?.status || log.status || 'new';
        return currentStatus !== 'canceled';
      });
    }
    return logs;
  };

  const cancelledCount = (errorLogs.logs || []).filter(log => {
    const s = errorLogMeta[log.id]?.status || log.status || 'new';
    return s === 'canceled';
  }).length;

  const toggleSelectLog = (id) => {
    setSelectedLogIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visible = getFilteredErrorLogs();
    if (selectedLogIds.size === visible.length) {
      setSelectedLogIds(new Set());
    } else {
      setSelectedLogIds(new Set(visible.map(l => l.id || String(l))));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedLogIds.size === 0) return;
    const ids = Array.from(selectedLogIds);
    try {
      await api.patch('/monitoring/errors/bulk', { log_ids: ids, status: bulkStatus });
      setErrorLogMeta(prev => {
        const next = { ...prev };
        ids.forEach(id => { next[id] = { ...(next[id] || {}), status: bulkStatus }; });
        return next;
      });
      toast.success(`Updated ${ids.length} log${ids.length > 1 ? 's' : ''} to "${bulkStatus}"`);
      setSelectedLogIds(new Set());
      setBulkStatus('');
    } catch {
      toast.error('Failed to bulk update status');
    }
  };

  const toggleStatusFilter = (value) => {
    setStatusFilter(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'error': return 'default';
      case 'warning': return 'secondary';
      default: return 'outline';
    }
  };

  const formatBytes = (mb) => {
    if (mb >= 1000) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/admin/system')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-serif text-xl font-semibold">System Monitoring</h1>
              <p className="text-xs text-white/70">Health, usage, and audit logs</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              invalidate();
            }}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="impersonation" className="space-y-6">
          <TabsList className="overflow-x-auto flex w-full">
            <TabsTrigger value="impersonation" className="whitespace-nowrap">
              <Eye className="w-4 h-4 mr-2" /> Impersonation Logs
            </TabsTrigger>
            <TabsTrigger value="resources" className="whitespace-nowrap">
              <Database className="w-4 h-4 mr-2" /> Resource Usage
            </TabsTrigger>
            <TabsTrigger value="errors" className="whitespace-nowrap">
              <AlertTriangle className="w-4 h-4 mr-2" /> Error Logs
            </TabsTrigger>
            <TabsTrigger value="repo-sync" className="whitespace-nowrap">
              <RefreshCw className="w-4 h-4 mr-2" /> Repository Sync
            </TabsTrigger>
          </TabsList>

          {/* Impersonation Logs */}
          <TabsContent value="impersonation">
            <Card>
              <CardHeader>
                <CardTitle>Impersonation Audit Trail ({impersonationLogs.length} sessions)</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : impersonationLogs.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground">No impersonation sessions logged</p>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {impersonationLogs.map(log => (
                      <div key={log.id} className="p-4 border rounded">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold">{log.tenant_name}</p>
                          <Badge variant={log.is_active ? 'default' : 'outline'}>
                            {log.is_active ? 'Active' : 'Ended'}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>System Owner: {log.system_owner_email}</p>
                          <p>Impersonated: {log.impersonated_user_email}</p>
                          <p>Started: {formatDateTime(log.start_time)}</p>
                          {log.end_time && <p>Ended: {formatDateTime(log.end_time)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resource Usage */}
          <TabsContent value="resources">
            <Card>
              <CardHeader>
                <CardTitle>Tenant Resource Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Track which tenants are using the most resources (storage, users, API calls, database size).
                </p>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : resourceUsage.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground">No tenant data available</p>
                ) : (
                  <div className="space-y-4">
                    {resourceUsage.map(tenant => (
                      <div key={tenant.tenant_id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-semibold text-lg">{tenant.tenant_name}</p>
                            <p className="text-xs text-muted-foreground">{tenant.tenant_id}</p>
                          </div>
                          <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>
                            {tenant.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="flex items-center gap-2">
                            <HardDrive className="w-4 h-4 text-blue-500" />
                            <div>
                              <p className="text-xs text-muted-foreground">Storage</p>
                              <p className="font-semibold">{formatBytes(tenant.storage_mb)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-green-500" />
                            <div>
                              <p className="text-xs text-muted-foreground">Users</p>
                              <p className="font-semibold">{tenant.user_count}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-purple-500" />
                            <div>
                              <p className="text-xs text-muted-foreground">API Calls (30d)</p>
                              <p className="font-semibold">{tenant.api_calls_30d.toLocaleString()}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Database className="w-4 h-4 text-orange-500" />
                            <div>
                              <p className="text-xs text-muted-foreground">DB Size (est.)</p>
                              <p className="font-semibold">{formatBytes(tenant.db_size_mb)}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                          <span className="mr-4">Horses: {tenant.horse_count}</span>
                          <span>Total Documents: {tenant.total_documents.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Error Logs */}
          <TabsContent value="errors">
            <Card>
              <CardHeader>
                <CardTitle>Global Error Logs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  {/* Summary Cards */}
                  {!loading && errorLogs.summary && (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-3 border rounded bg-red-50">
                        <p className="text-xs text-muted-foreground">Critical</p>
                        <p className="text-2xl font-bold text-red-600">{errorLogs.summary.critical || 0}</p>
                      </div>
                      <div className="p-3 border rounded bg-orange-50">
                        <p className="text-xs text-muted-foreground">Errors</p>
                        <p className="text-2xl font-bold text-orange-600">{errorLogs.summary.error || 0}</p>
                      </div>
                      <div className="p-3 border rounded bg-yellow-50">
                        <p className="text-xs text-muted-foreground">Warnings</p>
                        <p className="text-2xl font-bold text-yellow-600">{errorLogs.summary.warning || 0}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Filter Toolbar */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant={errorFilter === 'all' ? 'default' : 'outline'} onClick={() => setErrorFilter('all')}>All</Button>
                    <Button size="sm" variant={errorFilter === 'critical' ? 'default' : 'outline'} onClick={() => setErrorFilter('critical')}>Critical</Button>
                    <Button size="sm" variant={errorFilter === 'error' ? 'default' : 'outline'} onClick={() => setErrorFilter('error')}>Errors</Button>
                    <Button size="sm" variant={errorFilter === 'warning' ? 'default' : 'outline'} onClick={() => setErrorFilter('warning')}>Warnings</Button>
                    <div className="w-px h-5 bg-border mx-1" />
                    {ERROR_STATUSES.map(s => (
                      <button
                        key={s.value}
                        onClick={() => toggleStatusFilter(s.value)}
                        data-testid={`status-pill-${s.value}`}
                        className={`h-7 px-3 rounded-full text-xs font-medium border transition-colors ${
                          statusFilter.includes(s.value)
                            ? s.color + ' shadow-sm'
                            : 'bg-background text-muted-foreground border-input hover:border-foreground/40'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                    {statusFilter.length > 0 && (
                      <button onClick={() => setStatusFilter([])} className="h-7 px-2 rounded-full text-xs text-muted-foreground hover:text-foreground border border-transparent hover:border-input transition-colors">Clear</button>
                    )}
                    <div className="w-px h-5 bg-border mx-1" />
                    {/* Hide Cancelled toggle */}
                    <button
                      onClick={() => setHideCancelled(h => !h)}
                      data-testid="hide-cancelled-toggle"
                      className={`h-7 px-3 rounded-full text-xs font-medium border transition-colors ${
                        hideCancelled
                          ? 'bg-gray-100 text-gray-500 border-gray-300'
                          : 'bg-background text-muted-foreground border-input hover:border-foreground/40'
                      }`}
                    >
                      {hideCancelled
                        ? `Showing ${cancelledCount} cancelled hidden`
                        : 'Hide Cancelled'}
                    </button>
                  </div>

                  {/* Bulk Action Bar — visible when rows are selected */}
                  {selectedLogIds.size > 0 && (
                    <div className="flex items-center gap-3 mt-3 p-2 bg-primary/5 border border-primary/20 rounded-lg" data-testid="bulk-action-bar">
                      <span className="text-xs font-medium text-primary">{selectedLogIds.size} selected</span>
                      <Select value={bulkStatus} onValueChange={setBulkStatus}>
                        <SelectTrigger className="h-7 w-36 text-xs" data-testid="bulk-status-select">
                          <SelectValue placeholder="Set status…" />
                        </SelectTrigger>
                        <SelectContent>
                          {ERROR_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        className="h-7 bg-primary text-white"
                        onClick={handleBulkStatusUpdate}
                        disabled={!bulkStatus}
                        data-testid="bulk-apply-btn"
                      >
                        Apply
                      </Button>
                      <button onClick={() => setSelectedLogIds(new Set())} className="text-xs text-muted-foreground hover:text-foreground ml-1">Clear</button>
                    </div>
                  )}
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : getFilteredErrorLogs().length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground">No error logs found</p>
                ) : (
                  <div className="space-y-2 max-h-[640px] overflow-y-auto">
                    {/* Select-all row */}
                    <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        className="rounded accent-primary"
                        checked={selectedLogIds.size > 0 && selectedLogIds.size === getFilteredErrorLogs().length}
                        onChange={toggleSelectAll}
                        data-testid="select-all-logs"
                      />
                      <span>Select all {getFilteredErrorLogs().length} visible</span>
                    </div>
                    {getFilteredErrorLogs().map((log, index) => {
                      const logId = log.id || String(index);
                      const meta = errorLogMeta[logId] || { status: log.status || 'new', notes: log.notes || '' };
                      const isSelected = selectedLogIds.has(logId);
                      return (
                      <div key={logId} className={`p-3 border rounded-lg transition-colors ${isSelected ? 'border-primary/40 bg-primary/5' : ''}`}>
                        {/* Top row: checkbox + severity + tenant + timestamp */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="rounded accent-primary mt-0.5"
                              checked={isSelected}
                              onChange={() => toggleSelectLog(logId)}
                              data-testid={`select-log-${logId}`}
                              onClick={e => e.stopPropagation()}
                            />
                            <Badge variant={getSeverityColor(log.severity)}>
                              {log.severity?.toUpperCase()}
                            </Badge>
                            {log.tenant_name && (
                              <span className="text-xs text-muted-foreground">{log.tenant_name}</span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0 ml-2">
                            {formatDateTime(log.timestamp)}
                          </span>
                        </div>

                        <p className="text-sm font-medium mb-1">{log.message}</p>

                        {log.endpoint && (
                          <p className="text-xs text-muted-foreground mb-2">Endpoint: {log.endpoint}</p>
                        )}

                        {log.stack_trace && (
                          <details className="mb-2">
                            <summary className="text-xs text-blue-600 cursor-pointer">View Stack Trace</summary>
                            <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                              {log.stack_trace}
                            </pre>
                          </details>
                        )}

                        {/* Status + Notes row */}
                        <div className="mt-2 pt-2 border-t flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-12 shrink-0">Status</span>
                            <Select
                              value={meta.status || 'new'}
                              onValueChange={(val) => {
                                setErrorLogMeta(prev => ({ ...prev, [logId]: { ...prev[logId], status: val } }));
                                handleStatusChange(logId, val);
                              }}
                            >
                              <SelectTrigger
                                className={`h-6 text-xs w-32 border font-medium ${getStatusStyle(meta.status || 'new')}`}
                                data-testid={`error-status-${logId}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ERROR_STATUSES.map(s => (
                                  <SelectItem key={s.value} value={s.value} className="text-xs">
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {log.updated_by && (
                              <span className="text-xs text-muted-foreground ml-1">
                                · Updated by {log.updated_by}
                              </span>
                            )}
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-xs text-muted-foreground w-12 shrink-0 pt-1.5">Notes</span>
                            <Textarea
                              value={meta.notes ?? ''}
                              onChange={(e) =>
                                setErrorLogMeta(prev => ({ ...prev, [logId]: { ...prev[logId], notes: e.target.value } }))
                              }
                              placeholder="Add notes..."
                              rows={2}
                              className="text-xs resize-none flex-1 min-h-0 py-1.5"
                              data-testid={`error-notes-${logId}`}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 shrink-0 mt-0.5"
                              onClick={() => handleNotesSave(logId)}
                              disabled={meta.savingNotes}
                              data-testid={`error-notes-save-${logId}`}
                            >
                              {meta.savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* Repository Sync Log */}
          <TabsContent value="repo-sync">
            {/* Bulk Force Sync card */}
            <BulkForceSyncCard />
            {/* Per-Sale Force Sync card */}
            <PerSaleForceSyncCard />
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle>Repository Sync Log</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Most recent sync events across all tenants and sale companies.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={syncTenantFilter} onValueChange={setSyncTenantFilter}>
                      <SelectTrigger className="h-8 text-xs w-44">
                        <SelectValue placeholder="All Tenants" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tenants</SelectItem>
                        {tenants.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={syncCompanyFilter} onValueChange={setSyncCompanyFilter}>
                      <SelectTrigger className="h-8 text-xs w-40">
                        <SelectValue placeholder="All Companies" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Companies</SelectItem>
                        <SelectItem value="fasig">Fasig-Tipton</SelectItem>
                        <SelectItem value="obs">OBS</SelectItem>
                        <SelectItem value="keeneland">Keeneland</SelectItem>
                        <SelectItem value="inglis">Inglis</SelectItem>
                      </SelectContent>
                    </Select>
                    <input
                      type="text"
                      placeholder="Filter by sale name…"
                      value={syncSaleNameFilter}
                      onChange={e => setSyncSaleNameFilter(e.target.value)}
                      className="h-8 text-xs border rounded-md px-2.5 w-44 bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="sync-sale-name-filter"
                    />
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                      onClick={() => fetchSyncLogs(syncPage)} disabled={syncLogsLoading}>
                      {syncLogsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {syncLogsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : syncLogs.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground text-sm">
                    No sync history found for the selected filters.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-6">Time</TableHead>
                          <TableHead>Tenant</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Sale</TableHead>
                          <TableHead className="text-center">Fetched</TableHead>
                          <TableHead className="text-center">Created</TableHead>
                          <TableHead className="text-center">Skipped</TableHead>
                          <TableHead className="text-center">Not In System</TableHead>
                          <TableHead className="text-center">Trigger</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncLogs.map(l => (
                          <TableRow key={l.id} className="text-sm">
                            <TableCell className="pl-6 text-muted-foreground whitespace-nowrap">
                              {new Date(l.synced_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </TableCell>
                            <TableCell className="font-medium">{l.tenant_name || '—'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs font-normal">
                                {COMPANY_LABEL[l.company] || l.company}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[180px] truncate text-sm" title={l.sale_name || l.obs_url}>
                              {l.sale_name || (l.obs_url
                                ? <span className="font-mono text-xs text-muted-foreground">{l.obs_url.slice(0, 45)}…</span>
                                : '—')}
                            </TableCell>
                            <TableCell className="text-center">{l.viewings_fetched ?? l.records_fetched ?? '—'}</TableCell>
                            <TableCell className="text-center font-medium text-green-700">{l.procedures_created ?? '—'}</TableCell>
                            <TableCell className="text-center text-muted-foreground">{l.procedures_skipped ?? '—'}</TableCell>
                            <TableCell className="text-center">
                              {(l.hips_not_in_system ?? 0) > 0
                                ? <span className="text-amber-600 font-semibold">{l.hips_not_in_system}</span>
                                : <span className="text-muted-foreground">{l.hips_not_in_system ?? '—'}</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              {l.triggered_by === 'manual' || l.triggered_by?.startsWith('system_owner_force')
                                ? <div>
                                    <span className="text-xs text-blue-600 capitalize">{l.triggered_by?.startsWith('system_owner_force') ? (l.triggered_by === 'system_owner_force_backfill' ? 'backfill' : 'force') : 'manual'}</span>
                                    {l.backfill_from && (
                                      <p className="text-[10px] text-muted-foreground whitespace-nowrap">
                                        from {new Date(l.backfill_from).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                      </p>
                                    )}
                                  </div>
                                : <span className="text-xs text-muted-foreground">auto</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              {l.status === 'success'
                                ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" />
                                : l.status === 'error'
                                  ? <XCircle className="w-4 h-4 text-red-500 mx-auto" title={l.error_message} />
                                  : l.status === 'no_activity'
                                    ? <MinusCircle className="w-4 h-4 text-blue-400 mx-auto" title={`Sync ran — ${l.live_sales_count ?? 0} live sale(s) in window, 0 records from Fasig`} />
                                    : <Clock className="w-4 h-4 text-muted-foreground mx-auto" />}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {/* Pagination bar */}
                {syncTotal > SYNC_PAGE_SIZE && (
                  <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-muted-foreground">
                    <span>
                      {((syncPage - 1) * SYNC_PAGE_SIZE) + 1}–{Math.min(syncPage * SYNC_PAGE_SIZE, syncTotal)} of {syncTotal.toLocaleString()} records
                    </span>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        disabled={syncPage === 1 || syncLogsLoading}
                        onClick={() => { const p = syncPage - 1; setSyncPage(p); fetchSyncLogs(p); }}>
                        Previous
                      </Button>
                      <span className="px-2 text-xs">Page {syncPage} of {Math.ceil(syncTotal / SYNC_PAGE_SIZE)}</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        disabled={syncPage >= Math.ceil(syncTotal / SYNC_PAGE_SIZE) || syncLogsLoading}
                        onClick={() => { const p = syncPage + 1; setSyncPage(p); fetchSyncLogs(p); }}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MonitoringPage;
